import React, { useState, useEffect } from 'react'
import { Row, Col, Card, Statistic, Spin } from 'antd'
import {
  UserOutlined,
  ShopOutlined,
  DollarOutlined,
  ScheduleOutlined
} from '@ant-design/icons'
import ReactECharts from 'echarts-for-react'
import api from '../../utils/api'
import styled from 'styled-components'
import AdminTable from '../../components/common/AdminTable'

const StyledContainer = styled.div`
  padding: 24px;

  @media (max-width: 768px) {
    padding: 0px;
  }
`

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dashboardData, setDashboardData] = useState({
    totalShops: 0,
    totalUsers: 0,
    monthlyRevenue: 0,
    todayAppointments: 0,
    revenueTrend: [],
    shopCategoryStats: [],
    latestRegisteredShops: []
  })

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true)
        const response = await api.get(`/api/admin/dashboard-stats`)
        const data = response.data
        setDashboardData(data)
        setLoading(false)
      } catch (err) {
        console.error('Dashboard error:', err)
        setError(err.message)
        setLoading(false)
      }
    }
    fetchDashboardData()
  }, [])

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <Spin size='large' />
      </div>
    )
  }

  if (error) {
    return <div>Error: {error}</div>
  }

  // 數據統計卡片組件
  const StatisticCard = ({ title, value, icon, color }) => (
    <Card>
      <Statistic
        title={title}
        value={value}
        prefix={icon}
        valueStyle={{ color }}
      />
    </Card>
  )

  // 顏色映射函數
  const getColorByCategory = (category) => {
    const colorMap = {
      美髮沙龍: '#FF6B6B',
      按摩SPA: '#4ECDC4',
      美容護膚: '#45B7D1',
      睫毛美容: '#96CEB4',
      美甲美睫: '#FFEEAD',
      眉部美容: '#D4A5A5',
      除毛: '#9FA8DA',
      彩妝造型: '#FFD93D',
      其他: '#95A5A6'
    }
    return colorMap[category] || '#95A5A6' // 如果沒有對應顏色則返回默認顏色
  }

  // 營業額趨勢圖配置
  const revenueChartOption = {
    title: {
      text: '營業額趨勢'
    },
    tooltip: {
      trigger: 'axis'
    },
    xAxis: {
      type: 'category',
      data: dashboardData.revenueTrend.map((item) => item.month)
    },
    yAxis: {
      type: 'value',
      name: '營業額'
    },
    series: [
      {
        name: '營業額',
        type: 'line',
        smooth: true,
        data: dashboardData.revenueTrend.map((item) => item.revenue),
        itemStyle: {
          color: '#1890ff'
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: 'rgba(24,144,255,0.3)'
              },
              {
                offset: 1,
                color: 'rgba(24,144,255,0.1)'
              }
            ]
          }
        }
      }
    ]
  }

  // 店家分類餅圖配置
  const pieChartOption = {
    title: {
      text: '店家分類統計',
      left: 'center'
    },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)'
    },
    legend: {
      orient: 'vertical',
      left: 'left',
      type: 'scroll'
    },
    series: [
      {
        name: '店家類型',
        type: 'pie',
        radius: ['50%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: '#fff',
          borderWidth: 2
        },
        label: {
          show: true,
          position: 'outside',
          formatter: '{b}: {c} ({d}%)'
        },
        emphasis: {
          label: {
            show: true,
            fontSize: '16',
            fontWeight: 'bold'
          }
        },
        // 使用 dashboardData 中的 shopCategoryStats 數據
        data: dashboardData.shopCategoryStats.map((item) => ({
          value: item.value,
          name: item.category,
          itemStyle: {
            // 可以根據類別設置不同的顏色
            color: getColorByCategory(item.category)
          }
        }))
      }
    ]
  }

  return (
    <StyledContainer>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <StatisticCard
            title='總店家數'
            value={dashboardData.totalShops}
            icon={<ShopOutlined />}
            color='#1890ff'
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatisticCard
            title='總用戶數'
            value={dashboardData.totalUsers}
            icon={<UserOutlined />}
            color='#52c41a'
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatisticCard
            title='本月營業額'
            value={dashboardData.monthlyRevenue}
            icon={<DollarOutlined />}
            color='#faad14'
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatisticCard
            title='今日預約數'
            value={dashboardData.todayAppointments}
            icon={<ScheduleOutlined />}
            color='#722ed1'
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={24}>
          <Card>
            <ReactECharts
              option={revenueChartOption}
              style={{ height: '400px' }}
            />
          </Card>
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={24}>
          <Card>
            <ReactECharts option={pieChartOption} style={{ height: '400px' }} />
          </Card>
        </Col>
      </Row>

      <Card title='最新註冊店家' style={{ marginTop: 24 }}>
        <AdminTable
          columns={[
            { title: '店家名稱', dataIndex: 'name', key: 'name' },
            {
              title: '註冊時間',
              dataIndex: 'registerTime',
              key: 'registerTime'
            },
            { title: '地址', dataIndex: 'address', key: 'address' },
            { title: '聯絡電話', dataIndex: 'phone', key: 'phone' },
            { title: '商店狀態', dataIndex: 'status', key: 'status' }
          ]}
          dataSource={dashboardData.latestRegisteredShops || []}
          loading={false}
          pagination={{
            currentPage: 1,
            totalItems: dashboardData.latestRegisteredShops?.length || 0,
            itemsPerPage: 10
          }}
          rowKey='id'
        />
      </Card>
    </StyledContainer>
  )
}

export default AdminDashboard
